import { AxiosResponse } from 'axios';

import interceptor from '../common/interceptor';
import { APIPaths } from '../common/constants';
import { OperationType } from '../common/enum';

export type PickupCarrierType = 'GROUND' | 'EXPRESS';
export type PickupWeightUnit = 'LB' | 'KG';
export type PickupPackageLocation = 'FRONT' | 'REAR' | 'SIDE' | 'NONE';

export interface PickupAddress {
  street: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  residential: boolean;
}

export interface PickupContact {
  name: string;
  company: string;
  phone: string;
}

export interface PickupAccount {
  accountNumber: string;
  description: string;
  isDefault: boolean;
}

export interface FedExPickup {
  id: string;
  shipmentId: string;
  status: 'SCHEDULED' | 'CANCELLED' | 'FAILED';
  carrierType: PickupCarrierType;
  confirmationCode: string;
  pickupDate: string;
  readyTime: string;
  closeTime: string;
  accountNumber: string;
  packageCount: number;
  weight: number | null;
  weightUnit: PickupWeightUnit;
  address: PickupAddress;
  contact: PickupContact;
  packageLocation: PickupPackageLocation;
  remarks: string;
  canCancel: boolean;
  cancelBlockedReason: string | null;
  createdAt: string;
}

export interface FedExPickupPrefill {
  eligible: boolean;
  /** Manual entry is possible even when automatic scheduling is not. */
  manualAllowed: boolean;
  /** False when FedEx isn't linked (or no usable account number exists). */
  accountLinked: boolean;
  /** IANA timezone of the pickup origin. */
  timezone: string;
  /** YYYY-MM-DD in the origin timezone. */
  originToday: string;
  reasons: string[];
  carrierType: PickupCarrierType;
  accountNumber: string;
  accounts: PickupAccount[];
  address: PickupAddress;
  contact: PickupContact;
  weight: number | null;
  weightUnit: PickupWeightUnit;
  packageCount: number;
  activePickup: FedExPickup | null;
}

export interface PickupAvailabilityPayload {
  shipmentId: string;
  accountNumber: string;
  carrierType: PickupCarrierType;
  /** YYYY-MM-DD */
  dispatchDate: string;
  address: PickupAddress;
}

export interface PickupAvailabilityOption {
  carrier: 'FDXG' | 'FDXE';
  /** Derived by the backend from `carrier`, so the UI never handles FedEx codes. */
  carrierType: PickupCarrierType | null;
  available: boolean;
  requestedDate?: string;
  /** May differ from requestedDate: FedEx resolves weekends to the next working day. */
  pickupDate: string;
  /** HH:MM:SS */
  cutOffTime: string;
  /** null/undefined means unknown; only `false` blocks. */
  residentialAvailable?: boolean | null;
  readyTimeOptions: string[];
  defaultReadyTime?: string | null;
  latestTimeOptions: string[];
  defaultLatestTime?: string | null;
}

export interface PickupAvailabilityResponse {
  options: PickupAvailabilityOption[];
}

export interface SchedulePickupPayload {
  shipmentId: string;
  accountNumber: string;
  carrierType: PickupCarrierType;
  /** YYYY-MM-DD */
  pickupDate: string;
  /** HH:MM:SS */
  readyTime: string;
  /** HH:MM:SS */
  closeTime: string;
  address: PickupAddress;
  contact: PickupContact;
  packageCount: number;
  weight: number | null;
  weightUnit: PickupWeightUnit;
  packageLocation: PickupPackageLocation;
  remarks: string;
}

/** The backend's `return_class` body: `data` is the CustomResponse row, and its own `data` is the payload. */
interface ApiEnvelope<T> {
  data: {
    is_success: boolean;
    message?: string;
    status?: number;
    data: T;
  };
}

type PickupResponse<T> = AxiosResponse<ApiEnvelope<T>>;

export const GetFedExPickupPrefill = (
  token: string,
  shipmentId: string
): Promise<PickupResponse<FedExPickupPrefill>> =>
  new Promise((resolve, reject) => {
    interceptor
      .axiosGet(
        'Get',
        APIPaths.fedexPickupPrefill(shipmentId),
        token,
        undefined,
        undefined,
        true
      )
      .then((res) => resolve(res as PickupResponse<FedExPickupPrefill>))
      .catch((error) => reject(error?.response));
  });

export const CheckFedExPickupAvailability = (
  token: string,
  payload: PickupAvailabilityPayload
): Promise<PickupResponse<PickupAvailabilityResponse>> =>
  new Promise((resolve, reject) => {
    interceptor
      .axiosPost(
        'Post',
        APIPaths.fedexPickupAvailability,
        payload,
        token,
        undefined,
        // Read-only despite being a POST: preview sessions must still load it.
        OperationType.Read,
        true
      )
      .then((res) => resolve(res as PickupResponse<PickupAvailabilityResponse>))
      .catch((error) => reject(error?.response));
  });

export const ScheduleFedExPickup = (
  token: string,
  payload: SchedulePickupPayload
): Promise<PickupResponse<FedExPickup>> =>
  new Promise((resolve, reject) => {
    interceptor
      .axiosPost(
        'Post',
        APIPaths.fedexPickup,
        payload,
        token,
        undefined,
        OperationType.Write,
        true
      )
      .then((res) => resolve(res as PickupResponse<FedExPickup>))
      .catch((error) => reject(error?.response));
  });

export const CancelFedExPickup = (
  token: string,
  pickupId: string
): Promise<PickupResponse<FedExPickup>> =>
  new Promise((resolve, reject) => {
    interceptor
      .axiosPost(
        'Post',
        APIPaths.fedexPickupCancel,
        { pickupId },
        token,
        undefined,
        OperationType.Write,
        true
      )
      .then((res) => resolve(res as PickupResponse<FedExPickup>))
      .catch((error) => reject(error?.response));
  });

const FALLBACK_ERROR = 'FedEx pickup request failed';

/** Handles `{error}`, `{field: [msgs]}` and plain-message error bodies. */
export function getPickupErrorMessage(err: unknown): string {
  const e = err as {
    data?: {
      message?: string;
      data?: { message?: string; data?: unknown };
    };
    message?: string;
  };
  const payload = e?.data?.data?.data;
  if (typeof payload === 'string' && payload) return payload;
  if (payload && typeof payload === 'object') {
    const body = payload as Record<string, unknown>;
    if (typeof body.error === 'string' && body.error) return body.error;
    const fieldMessages = Object.entries(body).flatMap(([field, msgs]) => {
      const list = Array.isArray(msgs) ? msgs : [msgs];
      return list
        .filter((m): m is string => typeof m === 'string' && !!m)
        .map((m) => (field === 'non_field_errors' ? m : `${field}: ${m}`));
    });
    if (fieldMessages.length) return fieldMessages.join('. ');
  }
  return (
    e?.data?.data?.message || e?.data?.message || e?.message || FALLBACK_ERROR
  );
}
